import axios from 'axios';

// Configuración base para el servicio de usuarios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const userAPI = axios.create({
  baseURL: `${API_BASE_URL}/users`,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
userAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Control para evitar múltiples refresh simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para manejo de errores con refresh automático
userAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return userAPI(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const token = localStorage.getItem('authToken');
      if (!token) {
        isRefreshing = false;
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newToken = response.data.access_token;
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return userAPI(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Servicios de usuarios
export const userService = {
  // Obtener todos los usuarios (solo admin)
  getAllUsers: async () => {
    try {
      const response = await userAPI.get('/');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener los usuarios: ' + error.message);
    }
  },

  // Obtener un usuario por ID
  getUserById: async (id) => {
    try {
      const response = await userAPI.get(`/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener el usuario ${id}: ` + error.message);
    }
  },

  // Crear un nuevo usuario (solo admin)
  createUser: async (userData) => {
    try {
      const response = await userAPI.post('/', userData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error('El email ya está registrado');
      }
      throw new Error('Error al crear el usuario: ' + error.message);
    }
  },

  // Actualizar un usuario
  updateUser: async (id, userData) => {
    try {
      const response = await userAPI.patch(`/${id}`, userData);
      return response.data;
    } catch (error) {
      throw new Error(`Error al actualizar el usuario ${id}: ` + error.message);
    }
  },

  // Eliminar un usuario (solo admin)
  deleteUser: async (id) => {
    try {
      const response = await userAPI.delete(`/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al eliminar el usuario ${id}: ` + error.message);
    }
  },

  // Activar/desactivar un usuario (solo admin)
  toggleUserStatus: async (id, isActive) => {
    try {
      const response = await userAPI.patch(`/${id}`, { isActive });
      return response.data;
    } catch (error) {
      throw new Error(`Error al cambiar el estado del usuario ${id}: ` + error.message);
    }
  },

  // Buscar usuarios por email (solo admin)
  searchUsersByEmail: async (email) => {
    try {
      const response = await userAPI.get(`/search?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al buscar usuarios: ' + error.message);
    }
  },

  // Obtener usuarios por rol (solo admin)
  getUsersByRole: async (role) => {
    try {
      const response = await userAPI.get(`/role/${role}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener usuarios con rol ${role}: ` + error.message);
    }
  },

  // Obtener estadísticas de usuarios (solo admin)
  getUserStats: async () => {
    try {
      const response = await userAPI.get('/stats');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener estadísticas de usuarios: ' + error.message);
    }
  },

  // Obtener reservas de un usuario específico
  getUserReservations: async (userId) => {
    try {
      const response = await userAPI.get(`/${userId}/reservations`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener reservas del usuario ${userId}: ` + error.message);
    }
  },

  // Cambiar rol de un usuario (solo admin)
  changeUserRole: async (id, role) => {
    try {
      const response = await userAPI.patch(`/${id}/role`, { role });
      return response.data;
    } catch (error) {
      throw new Error(`Error al cambiar el rol del usuario ${id}: ` + error.message);
    }
  },

  // Activar cuenta con token y completar perfil
  activateAccount: async (token, userData) => {
    try {
      const response = await userAPI.post('/activate-account', {
        token,
        ...userData,
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Token inválido o expirado');
      }
      throw new Error('Error al activar la cuenta: ' + error.message);
    }
  },

  // Validar token de activación
  validateActivationToken: async (token) => {
    try {
      const response = await userAPI.get(`/validate-token/${token}`);
      return response.data;
    } catch (error) {
      throw new Error('Token inválido o expirado');
    }
  },
};

export default userService;